import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditContactSelectField } from '../../../EditContactSelectField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import {
  type AccountSummaryData,
  GET_ACCOUNT_SUMMARY
} from '../queries/accountSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountSummaryData;
}

const ACCOUNT_INFO_FIELDS: FieldMapping[] = [
  { apiField: 'name', formField: 'accountName' },
  { apiField: 'directInvesting', formField: 'directInvesting' },
  { apiField: 'isLpPlatform', formField: 'lpPlatform' },
  { apiField: 'smPortfolioFund', formField: 'starMountainPortfolioFund' },
  { apiField: 'smFundLender', formField: 'starMountainFundLender' },
  { apiField: 'smServiceProvider', formField: 'starMountainServiceProvider' },
  { apiField: 'primaryContactId', formField: 'primaryContactId' },
  { apiField: 'preferredEmailAddress', formField: 'preferredEmailAddress' },
  { apiField: 'mainPhone', formField: 'mainPhone' },
  { apiField: 'website', formField: 'website' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountSummaryData,
  initial: AccountSummaryData
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  for (const { apiField, formField } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    if (currentVal !== initialVal) {
      changes[apiField] = currentVal === '' ? null : currentVal;
    }
  }

  return changes;
}

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        name
        directInvesting
        isLpPlatform
        smPortfolioFund
        smFundLender
        smServiceProvider
        primaryContactId {
          id
          name
        }
        preferredEmailAddress
        phone
        website
      }
    }
  }
`;

export interface EditAccountInfoDialogProps {
  accountId: string;
  initialValue: AccountSummaryData;
  isOpen: boolean;
  onChange: (data: AccountSummaryData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountSummaryData;
}

export function EditAccountInfoDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditAccountInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_SUMMARY, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(ACCOUNT_INFO_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof AccountSummaryData>(
    field: K,
    fieldValue: AccountSummaryData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save account information');
      } else {
        toast.success('Account information saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving account information:', error);
      toast.error('Failed to save account information');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Account Information"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <EditFormField
          label="Account Name"
          onChange={v => {
            updateField('accountName', v);
          }}
          required
          value={value.accountName}
        />
        <EditCheckboxField
          checked={value.directInvesting}
          label="Direct Investing"
          onCheckedChange={checked => {
            updateField('directInvesting', checked);
          }}
        />
        <EditCheckboxField
          checked={value.lpPlatform}
          label="LP Platform"
          onCheckedChange={checked => {
            updateField('lpPlatform', checked);
          }}
        />
        <EditCheckboxField
          checked={value.starMountainPortfolioFund}
          label="Star Mountain Portfolio Fund"
          onCheckedChange={checked => {
            updateField('starMountainPortfolioFund', checked);
          }}
        />
        <EditCheckboxField
          checked={value.starMountainFundLender}
          label="Star Mountain Fund Lender"
          onCheckedChange={checked => {
            updateField('starMountainFundLender', checked);
          }}
        />
        <EditCheckboxField
          checked={value.starMountainServiceProvider}
          label="Star Mountain Service Provider"
          onCheckedChange={checked => {
            updateField('starMountainServiceProvider', checked);
          }}
        />
        <EditContactSelectField
          label="Primary Contact"
          onChange={(id, name) => {
            onChange({
              ...value,
              primaryContact: name,
              primaryContactId: id
            });
          }}
          value={
            value.primaryContactId
              ? { id: value.primaryContactId, name: value.primaryContact }
              : null
          }
        />
        <EditFormField
          label="Preferred Email"
          onChange={v => {
            updateField('preferredEmailAddress', v);
          }}
          type="email"
          value={value.preferredEmailAddress}
        />
        <EditFormField
          label="Main Phone"
          onChange={v => {
            updateField('mainPhone', v);
          }}
          type="tel"
          value={value.mainPhone}
        />
        <EditFormField
          label="Website"
          onChange={v => {
            updateField('website', v);
          }}
          type="url"
          value={value.website}
        />
      </div>
    </EditDialog>
  );
}
